import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogOut, ShieldCheck, Globe } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { LANGS } from "@/i18n/translations";
import { prefixFor } from "@/lib/langRoutes";
import { useSite } from "@/context/SiteContext";
import { scrollToId } from "@/components/SmoothScroll";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const LangSwitcher = ({ testid = "lang-switcher" }) => {
  const { lang } = useI18n();
  const { pathname } = useLocation(); // relative to basename (prefix stripped)
  const current = LANGS.find((l) => l.code === lang);
  const go = (code) => {
    if (code === lang) return;
    // The root URL represents Italian. Persist before navigating so the root
    // redirect cannot restore the previous language preference.
    localStorage.setItem("ananawi_lang", code);
    const base = pathname === "/" ? "/" : pathname;
    const url = `${prefixFor(code)}${base}${window.location.search}${window.location.hash}`;
    window.location.assign(url);
  };
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-1.5 rounded-full bg-white text-ink sticker-sm px-3 py-2 font-extrabold text-sm" data-testid={testid}>
          <Globe className="w-4 h-4" /> {current?.label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-2xl border-[2.5px] border-ink bg-white z-[80]">
        {LANGS.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => go(l.code)} className="font-bold cursor-pointer" data-testid={`lang-option-${l.code}`}>
            <span className="w-8">{l.label}</span> {l.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const useGo = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id) => {
    if (pathname === "/") scrollToId(id);
    else navigate(`/#${id}`);
  };
};

const UserChip = () => {
  const { t } = useI18n();
  const { user, logout } = useSite();
  if (!user) {
    return (
      <Link to="/login" className="btn-pop btn-pop-sm sticker-sm rounded-full bg-pink text-white px-4 py-2 font-display font-semibold" data-testid="nav-login-btn">
        {t.nav.login}
      </Link>
    );
  }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button className="rounded-full sticker-sm overflow-hidden w-10 h-10 bg-sun font-display font-bold" data-testid="nav-user-menu">
          {user.picture ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" /> : user.name?.[0]}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-2xl border-[2.5px] border-ink bg-white z-[80] min-w-[200px]">
        <div className="px-2 py-1.5 text-xs font-bold text-ink/70 truncate">{user.email}</div>
        <DropdownMenuItem asChild className="font-bold cursor-pointer"><Link to="/genitori" data-testid="nav-parents-link">{t.nav.parents}</Link></DropdownMenuItem>
        {user.is_admin && (
          <DropdownMenuItem asChild className="font-bold cursor-pointer"><Link to="/admin" data-testid="nav-admin-link"><ShieldCheck className="w-4 h-4 mr-2" />{t.nav.admin}</Link></DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={logout} className="font-bold cursor-pointer text-red-600" data-testid="nav-logout-btn"><LogOut className="w-4 h-4 mr-2" />{t.nav.logout}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const go = useGo();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const goHome = (e) => {
    if (pathname !== "/") return;
    e.preventDefault();
    if (hash) navigate("/", { replace: true });
    if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const pressLabel = ({ it: "Dicono di noi", en: "In the news", es: "Hablan de nosotros", pt: "Falam sobre nós" })[useI18n().lang] || "Stampa";
  const links = [["video", t.nav.video], ["musica", t.nav.music], ["disegna", t.nav.draw], ["galleria", t.nav.gallery], ["giochi", t.nav.games], ["genitori", t.nav.parents]];

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-3 sm:px-6 pt-3" data-testid="navbar">
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 120, damping: 18 }}
        className={`mx-auto max-w-7xl flex items-center gap-3 rounded-full pl-3 pr-2 py-2 transition-[background-color,box-shadow] duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl sticker-sm" : "bg-white/60 backdrop-blur-md border-[2.5px] border-ink/0"}`}
      >
        <Link to="/" onClick={goHome} aria-label="Torna alla homepage" title="Torna alla homepage" className="shrink-0 cursor-pointer" data-testid="nav-logo">
          <img src="/brand/logo-360.webp" width="360" height="239" alt="Ananawi" className="h-11 sm:h-12 w-auto transition-transform duration-300 hover:scale-110 hover:-rotate-3" />
        </Link>
        <div className="hidden lg:flex items-center gap-1 mx-auto">
          {links.map(([id, label]) => (
            <button key={id} onClick={() => go(id)} className="rounded-full px-3 xl:px-4 py-2 font-display font-semibold text-ink hover:bg-sun transition-colors duration-200 whitespace-nowrap" data-testid={`nav-link-${id}`}>
              {label}
            </button>
          ))}
          <Link to="/stampa" className="rounded-full px-3 xl:px-4 py-2 font-display font-semibold text-ink hover:bg-sun transition-colors duration-200 whitespace-nowrap" data-testid="nav-link-press">{pressLabel}</Link>
        </div>
        <div className="ml-auto lg:ml-0 flex items-center gap-2">
          <LangSwitcher />
          <UserChip />
          <button onClick={() => setOpen(true)} className="lg:hidden grid place-items-center w-10 h-10 rounded-full bg-sun sticker-sm" aria-label={t.nav.menu} data-testid="nav-mobile-open">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ clipPath: "circle(0% at 95% 5%)" }}
            animate={{ clipPath: "circle(150% at 95% 5%)" }}
            exit={{ clipPath: "circle(0% at 95% 5%)" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[70] bg-sun flex flex-col p-6"
            data-testid="nav-mobile-menu"
          >
            <div className="flex justify-between items-center">
              <img src="/brand/logo-360.webp" width="360" height="239" alt="Ananawi" className="h-12 w-auto" />
              <button onClick={() => setOpen(false)} className="grid place-items-center w-11 h-11 rounded-full bg-white sticker-sm" data-testid="nav-mobile-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-12 flex flex-col gap-3">
              {links.map(([id, label], i) => (
                <motion.button
                  key={id}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.15 + i * 0.07 }}
                  onClick={() => { setOpen(false); setTimeout(() => go(id), 350); }}
                  className="text-left font-display font-bold text-4xl text-ink"
                  data-testid={`nav-mobile-link-${id}`}
                >
                  {label}
                </motion.button>
              ))}
              <Link to="/stampa" onClick={() => setOpen(false)} className="text-left font-display font-bold text-4xl text-ink" data-testid="nav-mobile-link-press">{pressLabel}</Link>
            </div>
            <img src="/brand/winkwave.webp" alt="" className="mt-auto self-end w-40" />
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
